import { useState, useEffect } from 'react';
import {
  MapPin,
  MoreHorizontal,
  MessageSquare,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  Flag,
  CheckCircle,
  XCircle,
  Clock,
  EyeOff,
  Shield,
  AlertTriangle,
} from 'lucide-react';
import { User as FirebaseUser } from 'firebase/auth';
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { Avatar } from './ui/Avatar';
import { CommentsSection } from './CommentsSection';
import { ReportCategory, getCategoryLabel, getCategoryDescription } from '@/types/reports';
import { isElectionPeriod, getElectionPeriodWarning, requiresElectionScrutiny } from '@/lib/election-period';

interface ReportData {
  id: string;
  politician?: string;
  location: string;
  description?: string;
  imageUrl?: string;
  authorName?: string;
  authorPhoto?: string | null;
  createdAt?: any;
  userId?: string;
  status?: 'pending' | 'approved' | 'rejected';
  isPublic?: boolean;
  // Phase 2: Report category
  reportCategory?: ReportCategory;
  // DILG Anti-Epal Drive fields (for ANTI_EPAL category)
  isGovernmentFunded?: boolean;
  projectType?: 'government_project' | 'government_program' | 'government_activity' | 'government_property';
  projectName?: string;
  fundingSource?: 'national' | 'local' | 'both';
  violationType?: 'name' | 'image' | 'likeness' | 'combination';
  // Additional fields for other categories
  expectedTimeline?: string;
  serviceType?: string;
  complianceType?: string;
  communitySignals?: {
    validVotes: number;
    invalidVotes: number;
    abuseFlags: number;
    commentsCount: number;
  };
}

interface ReportCardProps {
  data: ReportData;
  isAdmin: boolean;
  onDelete: (reportId: string) => void;
  currentUser: FirebaseUser | null;
  userProfile: { displayName: string; photoUrl?: string | null } | null;
  onLoginReq: () => void;
}

export const ReportCard = ({
  data,
  isAdmin,
  onDelete,
  currentUser,
  userProfile,
  onLoginReq,
}: ReportCardProps) => {
  const [showComments, setShowComments] = useState(false);
  const [userVote, setUserVote] = useState<'valid' | 'invalid' | 'abuse' | null>(null);
  const [voting, setVoting] = useState(false);
  
  // Local state for vote counts (for optimistic updates)
  const [voteCounts, setVoteCounts] = useState({
    validVotes: data.communitySignals?.validVotes || 0,
    invalidVotes: data.communitySignals?.invalidVotes || 0,
    abuseFlags: data.communitySignals?.abuseFlags || 0,
    commentsCount: data.communitySignals?.commentsCount || 0,
  });

  // Sync vote counts when data prop changes (e.g., after page refresh)
  useEffect(() => {
    setVoteCounts({
      validVotes: data.communitySignals?.validVotes || 0,
      invalidVotes: data.communitySignals?.invalidVotes || 0,
      abuseFlags: data.communitySignals?.abuseFlags || 0,
      commentsCount: data.communitySignals?.commentsCount || 0,
    });
  }, [data.communitySignals]);

  // Check if user has already voted
  useEffect(() => {
    if (!currentUser || !data.id) return;
    const checkUserVote = async () => {
      try {
        const voteRef = doc(
          db,
          'artifacts',
          appId,
          'public',
          'data',
          'reports',
          data.id,
          'votes',
          currentUser.uid
        );
        const voteSnap = await getDoc(voteRef);
        if (voteSnap.exists()) {
          const voteData = voteSnap.data();
          if (voteData.type === 'valid') {
            setUserVote('valid');
          } else if (voteData.type === 'invalid') {
            setUserVote('invalid');
          } else if (voteData.type === 'abuse') {
            setUserVote('abuse');
          }
        }
      } catch (error: any) {
        // Silently handle permission errors - user may not have voted yet or rules may not be set up
        if (error?.code !== 'permission-denied') {
          console.error('[ReportCard] Error checking user vote:', error);
        }
      }
    };
    checkUserVote();
  }, [currentUser, data.id]);

  const handleVote = async (type: 'valid' | 'invalid' | 'abuse') => {
    if (!currentUser || !userProfile) {
      onLoginReq();
      return;
    }
    if (userVote === type) return; // Already voted this way

    setVoting(true);
    
    // Store previous state for error rollback
    const previousVoteState = userVote;
    const previousVoteCounts = { ...voteCounts };
    
    try {
      const reportRef = doc(
        db,
        'artifacts',
        appId,
        'public',
        'data',
        'reports',
        data.id
      );
      const voteRef = doc(
        db,
        'artifacts',
        appId,
        'public',
        'data',
        'reports',
        data.id,
        'votes',
        currentUser.uid
      );

      // Check existing vote
      const existingVote = await getDoc(voteRef);
      const previousType = existingVote.exists() ? existingVote.data().type : null;

      // Get current report data to check if communitySignals exists
      const reportSnap = await getDoc(reportRef);
      const reportData = reportSnap.exists() ? reportSnap.data() : null;

      // Update vote document first
      await setDoc(voteRef, {
        type,
        userId: currentUser.uid,
        userName: userProfile.displayName,
        createdAt: serverTimestamp(),
      });

      // Update community signals
      const updates: any = {};
      
      // Calculate the new vote counts
      const currentValidVotes = reportData?.communitySignals?.validVotes || voteCounts.validVotes || 0;
      const currentInvalidVotes = reportData?.communitySignals?.invalidVotes || voteCounts.invalidVotes || 0;
      const currentAbuseFlags = reportData?.communitySignals?.abuseFlags || voteCounts.abuseFlags || 0;
      const currentCommentsCount = reportData?.communitySignals?.commentsCount || voteCounts.commentsCount || 0;

      // Calculate new counts based on previous and new vote
      let newValidVotes = currentValidVotes;
      let newInvalidVotes = currentInvalidVotes;
      let newAbuseFlags = currentAbuseFlags;

      // Decrement previous vote type (only if there was a previous vote)
      if (previousType === 'valid') {
        newValidVotes = Math.max(0, newValidVotes - 1);
      } else if (previousType === 'invalid') {
        newInvalidVotes = Math.max(0, newInvalidVotes - 1);
      } else if (previousType === 'abuse') {
        newAbuseFlags = Math.max(0, newAbuseFlags - 1);
      }

      // Increment new vote type
      if (type === 'valid') {
        newValidVotes += 1;
      } else if (type === 'invalid') {
        newInvalidVotes += 1;
      } else if (type === 'abuse') {
        newAbuseFlags += 1;
      }

      // Optimistically update local state immediately
      setVoteCounts({
        validVotes: newValidVotes,
        invalidVotes: newInvalidVotes,
        abuseFlags: newAbuseFlags,
        commentsCount: currentCommentsCount,
      });
      setUserVote(type);

      // Set the complete communitySignals object
      updates['communitySignals'] = {
        validVotes: newValidVotes,
        invalidVotes: newInvalidVotes,
        abuseFlags: newAbuseFlags,
        commentsCount: currentCommentsCount,
      };

      await updateDoc(reportRef, updates);
    } catch (error: any) {
      console.error('[ReportCard] Error voting:', error);
      
      // Revert optimistic update on error
      setVoteCounts(previousVoteCounts);
      setUserVote(previousVoteState);
      
      // Show user-friendly error message
      if (error?.code === 'permission-denied') {
        const errorMessage = error?.message || '';
        if (errorMessage.includes('votes')) {
          alert('Permission denied: Firestore security rules need to allow votes subcollection. See docs/FIRESTORE_RULES_UPDATED.md');
        } else if (errorMessage.includes('update')) {
          alert('Permission denied: Firestore security rules need to allow users to update communitySignals field on reports. See docs/FIRESTORE_RULES_UPDATED.md');
        } else {
          alert('Permission denied. Please check Firestore security rules. See docs/FIRESTORE_RULES_UPDATED.md for required updates.');
        }
      } else if (error?.code === 'unavailable') {
        alert('Service temporarily unavailable. Please try again later.');
      } else {
        alert(`Failed to ${type === 'valid' ? 'upvote' : type === 'invalid' ? 'downvote' : 'report'} this post. Please try again.`);
      }
    } finally {
      setVoting(false);
    }
  };

  const getStatusBadge = () => {
    const status = data.status || 'pending';
    if (!isAdmin) return null;

    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-1 rounded">
            <Clock size={12} />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded">
            <CheckCircle size={12} />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded">
            <XCircle size={12} />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6 transition-transform hover:shadow-md">
      <div className="p-4 flex justify-between items-start">
        <div className="flex gap-3">
          <Avatar
            name={data.authorName || 'Anonymous'}
            url={data.authorPhoto}
            size="md"
          />
          <div>
            {/* Safer framing: Location/Project first, politician as observed detail */}
            <h3 className="font-bold text-gray-900">
              {data.projectName 
                ? `${data.projectName} - ${data.location}`
                : data.location 
                ? `Reported Observation - ${data.location}`
                : 'Reported Observation'}
            </h3>
            {/* Politician name as observed detail, not accusation */}
            {data.politician && (
              <div className="mt-2">
                <p className="text-sm text-gray-600 italic">
                  Name observed: <span className="font-medium not-italic">{data.politician}</span>
                </p>
                {/* Prominent disclaimer near politician name */}
                <div className="mt-1.5 bg-yellow-50 border-l-3 border-yellow-500 rounded px-2 py-1.5">
                  <p className="text-xs text-yellow-800 leading-relaxed">
                    <strong className="font-semibold">Legal Notice:</strong> This is a citizen-submitted observation of evidence. 
                    The name displayed was observed in the uploaded evidence. VerifyPH does not make legal judgments, 
                    accusations, or official determinations. This is not a legal finding of wrongdoing.
                  </p>
                </div>
              </div>
            )}
            <div className="flex items-center text-xs text-gray-500 gap-1 flex-wrap mt-1">
              <span className="font-medium text-blue-600">
                @{data.authorName || 'Anonymous'}
              </span>
              <span className="mx-1">•</span>
              <MapPin size={12} />
              <span>{data.location}</span>
              <span className="mx-1">•</span>
              <span>
                {data.createdAt?.toDate().toLocaleDateString() || 'Just now'}
              </span>
            </div>
            {/* Phase 2: Category Badge */}
            {data.reportCategory && (
              <div className="flex items-center gap-2 text-xs text-blue-600 mt-1.5">
                <span 
                  className="font-semibold"
                  title={data.reportCategory === 'ANTI_EPAL' 
                    ? 'Reports documenting observed promotional materials on government-funded projects.'
                    : getCategoryDescription(data.reportCategory)}
                >
                  {getCategoryLabel(data.reportCategory)}
                </span>
              </div>
            )}
            {/* DILG Compliance Info - Only for ANTI_EPAL */}
            {data.reportCategory === 'ANTI_EPAL' && data.isGovernmentFunded && (
              <div className="flex items-center gap-2 text-xs text-blue-600 mt-1.5 flex-wrap">
                <Shield size={12} className="flex-shrink-0" />
                <span className="font-semibold">Anti-Epal Watch</span>
                {data.projectType && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="capitalize">
                      {data.projectType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </>
                )}
                {data.fundingSource && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span>
                      {data.fundingSource === 'national' 
                        ? 'National' 
                        : data.fundingSource === 'local' 
                        ? 'Local' 
                        : 'National & Local'} Funded
                    </span>
                  </>
                )}
                {data.violationType && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="capitalize">
                      {data.violationType === 'combination' 
                        ? 'Name + Image/Likeness Observed' 
                        : `${data.violationType.charAt(0).toUpperCase() + data.violationType.slice(1)} Observed`}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge()}
          {data.isPublic === false && isAdmin && (
            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded">
              <EyeOff size={12} />
              Hidden
            </span>
          )}
          {isAdmin ? (
            <button
              onClick={() => onDelete(data.id)}
              className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
              title="Delete Report"
            >
              <Trash2 size={20} />
            </button>
          ) : (
            <button className="text-gray-400 hover:text-gray-600">
              <MoreHorizontal size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="relative bg-gray-100 aspect-video w-full overflow-hidden">
        {data.imageUrl ? (
          <img
            src={data.imageUrl}
            alt="Evidence"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            No Image
          </div>
        )}
        {data.reportCategory === 'ANTI_EPAL' && (
          <div className="absolute top-3 right-3 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded shadow-sm">
            Anti-Epal Watch
          </div>
        )}
      </div>

      <div className="p-4">
        <p className="text-gray-700 text-sm mb-3">
          {data.description || (
            <span className="italic text-gray-400">No description provided.</span>
          )}
        </p>
        
        {/* Election Period Warning - Most Prominent */}
        {isElectionPeriod() && requiresElectionScrutiny(data.reportCategory, data.politician) && (
          <div className="mb-3 bg-red-50 border-l-4 border-red-600 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={16} />
              <div>
                <p className="text-xs font-semibold text-red-900 mb-1">
                  Election Period Notice
                </p>
                <p className="text-xs text-red-800 leading-relaxed">
                  {getElectionPeriodWarning()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Report Disclaimer */}
        <div className={`mb-3 ${isElectionPeriod() ? 'bg-yellow-50 border-l-4 border-yellow-500' : 'bg-gray-50 border border-gray-200'} rounded-lg p-2.5`}>
          <p className={`text-xs ${isElectionPeriod() ? 'text-yellow-900 font-medium' : 'text-gray-600 italic'} leading-relaxed`}>
            {isElectionPeriod() ? (
              <>
                <strong>Legal Disclaimer:</strong> VerifyPH documents citizen-submitted evidence. 
                It does not make legal judgments, accusations, or official determinations. 
                This content does not represent an endorsement or opposition to any political candidate or party.
              </>
            ) : (
              'VerifyPH documents citizen-submitted evidence. It does not make legal judgments, accusations, or official determinations.'
            )}
          </p>
        </div>
        
        {/* Anti-Epal Category Disclaimer */}
        {data.reportCategory === 'ANTI_EPAL' && (
          <div className={`mb-3 ${isElectionPeriod() ? 'bg-orange-50 border-l-4 border-orange-500' : 'bg-blue-50 border border-blue-200'} rounded-lg p-2.5`}>
            <p className={`text-xs ${isElectionPeriod() ? 'text-orange-900 font-medium' : 'text-blue-700 italic'} leading-relaxed`}>
              {isElectionPeriod() ? (
                <>
                  <strong>Anti-Epal Watch:</strong> This report documents observed promotional materials on government-funded projects 
                  for transparency and public reference only. During election periods, this content is displayed as a neutral 
                  documentation of citizen observations and does not constitute electioneering or partisan activity.
                </>
              ) : (
                'Anti-Epal Watch documents observed promotional materials on government-funded projects for transparency and public reference only.'
              )}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <div className="flex gap-4">
            <button
              onClick={() => setShowComments(!showComments)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                showComments
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-blue-500'
              }`}
            >
              <MessageSquare size={18} />
              <span>Discuss</span>
              {voteCounts.commentsCount > 0 && (
                <span className="text-xs">({voteCounts.commentsCount})</span>
              )}
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleVote('valid')}
                disabled={voting || userVote === 'valid'}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  userVote === 'valid'
                    ? 'text-green-600'
                    : 'text-gray-500 hover:text-green-600'
                } disabled:opacity-50`}
                title="Upvote this post"
              >
                <ThumbsUp size={18} />
                <span>Upvote</span>
                {voteCounts.validVotes > 0 && (
                  <span className="text-xs font-semibold">+{voteCounts.validVotes}</span>
                )}
              </button>
              <button
                onClick={() => handleVote('invalid')}
                disabled={voting || userVote === 'invalid'}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  userVote === 'invalid'
                    ? 'text-amber-600'
                    : 'text-gray-500 hover:text-amber-600'
                } disabled:opacity-50`}
                title="Downvote this post"
              >
                <ThumbsDown size={18} />
                <span>Downvote</span>
                {voteCounts.invalidVotes > 0 && (
                  <span className="text-xs font-semibold">-{voteCounts.invalidVotes}</span>
                )}
              </button>
              <div className="h-5 w-px bg-gray-300"></div>
              <button
                onClick={() => handleVote('abuse')}
                disabled={voting || userVote === 'abuse'}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  userVote === 'abuse'
                    ? 'text-red-600'
                    : 'text-gray-500 hover:text-red-600'
                } disabled:opacity-50`}
                title="Report this post"
              >
                <Flag size={18} />
                <span>Report</span>
                {voteCounts.abuseFlags > 0 && (
                  <span className="text-xs">({voteCounts.abuseFlags})</span>
                )}
              </button>
            </div>
          </div>
          <div className="text-xs text-gray-400">ID: {data.id.slice(0, 6)}</div>
        </div>
      </div>

      {showComments && (
        <CommentsSection
          reportId={data.id}
          currentUser={currentUser}
          userProfile={userProfile}
          onLoginReq={onLoginReq}
        />
      )}
    </div>
  );
};
